import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TreeData } from "@src/components/tree/TreeTypes";

export interface SelectedResourceData {
  id: string | number | null;
  tutorialExist: boolean;
  assessmentExist: boolean;
  assignmentExist: boolean;
  practiceQuestionExist: boolean;
  videoAccessExist: boolean;
  assessmentMandatory: boolean;
  assignmentMandatory: boolean;
  videoMandatory: boolean;
  practiceMandatory: boolean;
  videoAccess: any;
  totalDuration: number | string | null;
  completedDuration: number | string | null;
  status: string | null;
  allAssessmentCompleted: boolean | null;
  allAssignmentCompleted: boolean | null;
  allPracticeCompleted: boolean | null;
  allVideoCompleted: boolean | null;
  videoStatus: boolean | string | null;
  orderOfFeatures: string[] | null;
  interviewLimitExceeded: boolean | null;
  isObjectiveExists: boolean;
  docId?: string | null;
}

interface InitialState {
  parentTree: TreeData[];
  pageTree: TreeData[];
  selectedNode: TreeData | null;
  selectedNodeName: string;
  resourceId: string;
  resourceType: string;
  classroomExpandedItems: string[];
  classroomSelectedItemId: string;
  selectedResourceData: SelectedResourceData | null;
  resourceCache: Record<string, SelectedResourceData | null>;
}

interface CacheResourceDataPayload {
  resourceId: string;
  selectedResourceData: SelectedResourceData | null;
}

const initialState: InitialState = {
  parentTree: [],
  pageTree: [],
  selectedNode: null,
  selectedNodeName: "",
  resourceId: "",
  resourceType: "",
  classroomExpandedItems: [],
  classroomSelectedItemId: "",
  selectedResourceData: null,
  resourceCache: {},
};

const addChildrenRecursively = (
  tree: TreeData[],
  parentId: string,
  children: TreeData[],
): TreeData[] => {
  return tree.map((node) => {
    if (node._id === parentId) {
      return {
        ...node,
        expanded: node.hasChildren ? true : false,
        children: children,
      };
    } else if (node.children) {
      return {
        ...node,
        children: addChildrenRecursively(node.children, parentId, children),
      };
    }
    return node;
  });
};

const toggleExpandedById = (
  arr: TreeData[] | undefined,
  parentId: string | null,
): TreeData[] => {
  return arr
    ? arr.map((item) => {
        if (Array.isArray(item.children)) {
          const updatedItem = {
            ...item,
            expanded: item._id === parentId ? !item.expanded : item.expanded,
            children: toggleExpandedById(
              item.children,
              item._id === parentId && !item.expanded ? null : parentId,
            ),
          };

          // If parent is collapsed, set all children to false
          if (item._id === parentId && !updatedItem.expanded) {
            updatedItem.children = updatedItem.children.map((child) => ({
              ...child,
              expanded: false,
              children: toggleExpandedById(child.children, null),
            }));
          }

          return updatedItem;
        }
        return item;
      })
    : [];
};

const updateVideoStatusRecursively = (
  tree: TreeData[],
  nodeId: string,
  videoStatus: boolean,
): TreeData[] => {
  return tree.map((node) => {
    if (node._id === nodeId || (node as any)?.id === nodeId) {
      return {
        ...node,
        videoStatus,
      };
    }
    if (node.children && node.children.length > 0) {
      return {
        ...node,
        children: updateVideoStatusRecursively(
          node.children,
          nodeId,
          videoStatus,
        ),
      };
    }
    return node;
  });
};

const classroomSidebarSlice = createSlice({
  name: "classroomSidebar",
  initialState: initialState,
  reducers: {
    setPageTreeData: (state, action) => {
      state.pageTree = action.payload;
    },
    setParentTreeData: (state, action) => {
      state.parentTree = action.payload;
    },
    updateSelectedParent: (state, action) => {
      state.parentTree = action.payload;
    },
    setSelectedNode: (state, action) => {
      state.selectedNode = action.payload;
      if (state.selectedNode && state.selectedResourceData) {
        const nodeType = state.selectedNode.type?.toUpperCase();
        if (nodeType === "CHAPTER" || nodeType === "TOPIC" || nodeType === "SUBTOPIC") {
          const docId = (state.selectedResourceData as any).docId;
          if (docId) {
            state.selectedNode = {
              ...state.selectedNode,
              docId: docId
            };
          }
        }
      }
    },
    addChildrenToPageTree: (state, action) => {
      const { parentId, children } = action.payload;
      state.pageTree = addChildrenRecursively(
        state.pageTree,
        parentId,
        children,
      );
    },
    addChildrenToParentTree: (state, action) => {
      const { parentId, children } = action.payload;
      state.parentTree = addChildrenRecursively(
        state.parentTree,
        parentId,
        children,
      );
    },
    collapseExpandInPageTree: (state, action) => {
      state.pageTree = toggleExpandedById(state.pageTree, action.payload._id);
    },
    collapseExpandInParentTree: (state, action) => {
      state.parentTree = toggleExpandedById(
        state.parentTree,
        action.payload._id,
      );
    },
    setSelectedNodeName: (state, action) => {
      state.selectedNodeName = action.payload;
    },
    setResourceDataForClassroom: (
      state,
      action: PayloadAction<Partial<InitialState>>,
    ) => {
      console.log("[classroomSidebarSlice] setResourceDataForClassroom action.payload:", action.payload);
      const updatedState = { ...state, ...action.payload };
      if (updatedState.selectedNode && updatedState.selectedResourceData) {
        const nodeType = updatedState.selectedNode.type?.toUpperCase();
        if (nodeType === "CHAPTER" || nodeType === "TOPIC" || nodeType === "SUBTOPIC") {
          const docId = (updatedState.selectedResourceData as any).docId;
          if (docId) {
            updatedState.selectedNode = {
              ...updatedState.selectedNode,
              docId: docId
            };
          }
        }
      }
      const targetId =
        action.payload.resourceId ||
        (action.payload.selectedResourceData as any)?.id ||
        (action.payload.selectedResourceData as any)?._id ||
        state.resourceId ||
        state.selectedNode?._id;

      if (
        action.payload.selectedResourceData?.videoStatus !== undefined &&
        action.payload.selectedResourceData?.videoStatus !== null &&
        targetId
      ) {
        const statusBool =
          action.payload.selectedResourceData.videoStatus === true ||
          action.payload.selectedResourceData.videoStatus === "true";
        updatedState.pageTree = updateVideoStatusRecursively(
          updatedState.pageTree,
          targetId,
          statusBool,
        );
        updatedState.parentTree = updateVideoStatusRecursively(
          updatedState.parentTree,
          targetId,
          statusBool,
        );
      }
      return updatedState;
    },
    updateNodeVideoStatus: (
      state,
      action: PayloadAction<{ nodeId: string; videoStatus: boolean }>,
    ) => {
      const { nodeId, videoStatus } = action.payload;
      if (!nodeId) return;
      state.pageTree = updateVideoStatusRecursively(
        state.pageTree,
        nodeId,
        videoStatus,
      );
      state.parentTree = updateVideoStatusRecursively(
        state.parentTree,
        nodeId,
        videoStatus,
      );
      if (state.resourceCache && state.resourceCache[nodeId]) {
        state.resourceCache[nodeId] = {
          ...state.resourceCache[nodeId]!,
          videoStatus,
        };
      }
      if (
        state.selectedResourceData &&
        (state.selectedResourceData.id === nodeId ||
          (state.selectedResourceData as any)._id === nodeId)
      ) {
        state.selectedResourceData = {
          ...state.selectedResourceData,
          videoStatus,
        };
      }
    },
    setCachedResourceDataForClassroom: (
      state,
      action: PayloadAction<CacheResourceDataPayload>,
    ) => {
      const { resourceId, selectedResourceData } = action.payload;

      if (!resourceId) {
        return;
      }

      if (!state.resourceCache) {
        state.resourceCache = {};
      }

      state.resourceCache[resourceId] = selectedResourceData;

      if (
        selectedResourceData?.videoStatus !== undefined &&
        selectedResourceData?.videoStatus !== null
      ) {
        const statusBool =
          selectedResourceData.videoStatus === true ||
          selectedResourceData.videoStatus === "true";
        state.pageTree = updateVideoStatusRecursively(
          state.pageTree,
          resourceId,
          statusBool,
        );
        state.parentTree = updateVideoStatusRecursively(
          state.parentTree,
          resourceId,
          statusBool,
        );
      }
    },
    resetClassroomTree: (state) => {
      state.pageTree = [];
      state.parentTree = [];
      state.selectedNode = null;
      state.classroomExpandedItems = [];
      state.classroomSelectedItemId = "";
    },
    resetClassroomSidebar: () => initialState,
    setClassroomSelectedItemId: (state, action) => {
      state.classroomSelectedItemId = action.payload;
    },
    setClassroomExpandedItems: (state, action) => {
      state.classroomExpandedItems = action.payload;
    },
  },
});

export const {
  setPageTreeData,
  updateSelectedParent,
  addChildrenToPageTree,
  addChildrenToParentTree,
  setParentTreeData,
  collapseExpandInParentTree,
  collapseExpandInPageTree,
  setSelectedNode,
  resetClassroomTree,
  resetClassroomSidebar,
  setSelectedNodeName,
  setResourceDataForClassroom,
  setCachedResourceDataForClassroom,
  updateNodeVideoStatus,
  setClassroomExpandedItems,
  setClassroomSelectedItemId,
} = classroomSidebarSlice.actions;
export default classroomSidebarSlice.reducer;
